/**
 * @module composables/useTags
 *
 * Composable for the episode tagging system.
 *
 * Holds the user's tag list and the episode→tags assignment map as
 * module-scoped singletons (same pattern as the other composables), so every
 * view — Latest, Episodes, Playlist, History, Search — shares one copy and
 * chips update everywhere the moment a tag is toggled.
 *
 * Data model:
 * - `tags`: all tags visible to the user. System tags (is_system, currently
 *   just "Star") are shared and locked; the rest are the user's own tags.
 * - `episodeTagIds`: plain reactive object mapping episode_id → array of
 *   tag ids. Built once from GET /tags/assignments and then maintained
 *   locally as tags are attached/detached, so no episode-list endpoint had
 *   to change to support chips.
 *
 * The Star tag is deliberately EXCLUDED from chips and from the tag-picker
 * dialog: starring keeps its dedicated yellow-star UI (swipe / context menu),
 * and the server keeps episodes.starred in sync with the Star tag.
 */

import { ref, computed } from "vue";

import { tagsApi } from "../api/tags.js";

// =============================================================================
// Module-scoped singleton state
// =============================================================================

/** All tags visible to the user (system tags first, then own tags A→Z) */
const tags = ref([]);

/**
 * Assignment map: episode_id → array of tag ids attached to that episode.
 * Plain reactive object (not Map) so Vue tracks per-key additions/removals
 * and each row's chip list only re-renders when its own entry changes.
 * DIRECT tags only — inherited podcast tags live in episodeInheritedTagIds.
 */
const episodeTagIds = ref({});

/**
 * Podcast-tag assignment map: podcast_id → array of tag ids labelling that
 * podcast. Drives the podcast tag picker and podcast chips. Maintained locally
 * (optimistically) as podcast tags are toggled.
 */
const podcastTagIds = ref({});

/**
 * INHERITED assignment map: episode_id → array of tag ids the episode inherits
 * from the podcast(s) it belongs to. Computed server-side (podcast_episodes ⋈
 * podcast_tags) and merged into chips/filters so a podcast label shows on every
 * one of its episodes. Read-only from the UI's perspective — it is refetched
 * whenever a podcast's tags change (podcast tags are the single source of truth).
 */
const episodeInheritedTagIds = ref({});

/** True while the initial tags+assignments load is in flight */
const loadingTags = ref(false);

/** True once tags+assignments have been loaded at least once this session */
const tagsLoaded = ref(false);

/** Snackbar state for tag operation feedback (consumed by dialogs/settings) */
const tagsSnackbar = ref({
  show: false,
  message: null,
  color: "info",
});

// =============================================================================
// Derived state
// =============================================================================

/** The user's own (non-system) tags — what the picker dialog and chips show */
const userTags = computed(() => tags.value.filter((tag) => !tag.is_system));

/** The shared system "Star" tag (locked row in the Settings table) */
const starTag = computed(
  () => tags.value.find((tag) => tag.is_system && tag.name.toLowerCase() === "star") || null,
);

/**
 * Sorts tags by usage (episode_count) descending, ties broken alphabetically
 * (case-insensitive). Shared by the usage-sorted computeds below.
 */
const byUsageDesc = (a, b) => {
  const diff = (b.episode_count || 0) - (a.episode_count || 0);
  return diff !== 0 ? diff : a.name.toLowerCase().localeCompare(b.name.toLowerCase());
};

/**
 * The user's own tags sorted by usage (most-tagged first) — used by the
 * assign/bulk-tag picker dialogs so the tags you actually use bubble to the
 * top instead of getting lost alphabetically.
 */
const userTagsByUsage = computed(() => [...userTags.value].sort(byUsageDesc));

/**
 * The user's own tags sorted by PODCAST usage (most-used-on-podcasts first),
 * ties broken alphabetically — used by the podcast search dialog's "Filter by
 * tag" field, where sorting by episode usage would bury the tags that actually
 * label podcasts. Star is excluded (podcasts carry user tags only).
 */
const userTagsByPodcastUsage = computed(() =>
  [...userTags.value].sort((a, b) => {
    const diff = (b.podcast_count || 0) - (a.podcast_count || 0);
    return diff !== 0 ? diff : a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  }),
);

/**
 * ALL visible tags (Star pinned first, then the user's own tags by usage) —
 * used by the "Filter by tag" pickers, where Star is a valid filter target.
 */
const tagsByUsage = computed(() => {
  const system = tags.value.filter((tag) => tag.is_system);
  return [...system, ...userTagsByUsage.value];
});

// =============================================================================
// Internal helpers
// =============================================================================

/**
 * Shows an error message in the tags snackbar.
 * @param {string} message - Human-readable error text
 */
const notifyError = (message) => {
  tagsSnackbar.value = { show: true, message, color: "error" };
};

/**
 * Re-sorts the local tag list to match the server ordering:
 * system tags first, then the user's tags alphabetically (case-insensitive).
 * Called after local inserts/renames so the Settings table stays ordered
 * without a refetch.
 */
const sortTags = () => {
  tags.value.sort((a, b) => {
    // System tags (Star) always sort before user tags
    if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
    // Within each group, alphabetical by name (case-insensitive)
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
};

/**
 * Builds an episode_id → [tag ids] map from flat inherited assignment pairs.
 * Shared by the initial load and every reload after a podcast-tag change.
 *
 * @param {Array<{episode_id:number, tag_id:number}>} pairs
 * @returns {Object} episode_id → array of inherited tag ids
 */
const buildInheritedMap = (pairs) => {
  const map = {};
  for (const { episode_id, tag_id } of pairs) {
    if (!map[episode_id]) map[episode_id] = [];
    map[episode_id].push(tag_id);
  }
  return map;
};

// =============================================================================
// Loading
// =============================================================================

/**
 * Loads the tag list and the episode→tags assignment map from the server.
 * By default it only fetches once per session (subsequent calls are no-ops)
 * since the map is maintained locally after every mutation; pass
 * `{ force: true }` to refetch (used by the Settings page so counts are fresh).
 *
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - Refetch even if already loaded
 */
const loadTags = async ({ force = false } = {}) => {
  // Skip when already loaded (unless forced) or a load is already in flight
  if ((tagsLoaded.value && !force) || loadingTags.value) return;

  loadingTags.value = true;
  try {
    // Fetch the tag list plus the three assignment sets in parallel:
    // direct episode tags, podcast tags, and inherited (podcast→episode) tags.
    const [tagList, assignments, podcastAssignments, inheritedAssignments] =
      await Promise.all([
        tagsApi.getTags(),
        tagsApi.getTagAssignments(),
        tagsApi.getPodcastTagAssignments(),
        tagsApi.getInheritedTagAssignments(),
      ]);

    // Replace the tag list wholesale (server returns it already sorted)
    tags.value = tagList;

    // Rebuild the episode→tag-ids map from the flat assignment pairs
    const map = {};
    for (const { episode_id, tag_id } of assignments) {
      // Initialize the episode's array on first sight, then append
      if (!map[episode_id]) map[episode_id] = [];
      map[episode_id].push(tag_id);
    }
    episodeTagIds.value = map;

    // Rebuild the podcast→tag-ids map
    const pmap = {};
    for (const { podcast_id, tag_id } of podcastAssignments) {
      if (!pmap[podcast_id]) pmap[podcast_id] = [];
      pmap[podcast_id].push(tag_id);
    }
    podcastTagIds.value = pmap;

    // Rebuild the inherited episode→tag-ids map
    episodeInheritedTagIds.value = buildInheritedMap(inheritedAssignments);

    tagsLoaded.value = true;
  } catch (error) {
    // Leave prior state intact on failure; surface the problem via snackbar
    console.error("Failed to load tags:", error);
    notifyError(error.message);
  } finally {
    loadingTags.value = false;
  }
};

// =============================================================================
// Per-episode helpers (used by chips + the tag picker dialog)
// =============================================================================

/**
 * Returns the tag objects to render as chips for an episode, EXCLUDING the
 * system Star tag. Combines the episode's DIRECT tags with the tags it INHERITS
 * from its podcast: direct tags come first (plain), then inherited-only tags
 * (annotated `inherited: true` so the chip row can style them differently). A
 * tag that is both direct and inherited is shown once, as direct.
 *
 * @param {number} episodeId - The episode's database id
 * @returns {Array} Array of tag objects to render as chips (possibly empty)
 */
const tagsForEpisode = (episodeId) => {
  // DIRECT tag ids attached to this episode (empty when untagged)
  const directIds = episodeTagIds.value[episodeId] || [];
  // INHERITED tag ids from the episode's podcast(s)
  const inheritedIds = episodeInheritedTagIds.value[episodeId] || [];

  // Direct chips: resolve ids to the user's own tags (userTags is pre-sorted,
  // so filtering preserves alphabetical order). System tags never appear.
  const direct = userTags.value.filter((tag) => directIds.includes(tag.id));

  // Inherited chips: only tags NOT already shown as direct, flagged inherited.
  const directIdSet = new Set(direct.map((t) => t.id));
  const inherited = userTags.value
    .filter((tag) => inheritedIds.includes(tag.id) && !directIdSet.has(tag.id))
    .map((tag) => ({ ...tag, inherited: true }));

  return [...direct, ...inherited];
};

/**
 * Whether a specific tag is DIRECTLY attached to an episode (ignores podcast
 * inheritance). Used by the tag picker, which edits direct tags only.
 *
 * @param {number} episodeId - The episode's database id
 * @param {number} tagId - The tag's database id
 * @returns {boolean} True when the tag is directly attached
 */
const episodeHasTag = (episodeId, tagId) => {
  return (episodeTagIds.value[episodeId] || []).includes(tagId);
};

/**
 * Whether an episode EFFECTIVELY has a tag — directly OR inherited from its
 * podcast. Used by the client-side tag filters (Playlist, History) so filtering
 * by a podcast label matches every episode that inherits it.
 *
 * @param {number} episodeId - The episode's database id
 * @param {number} tagId - The tag's database id
 * @returns {boolean} True when the tag is attached directly or inherited
 */
const episodeHasTagEffective = (episodeId, tagId) => {
  return (
    (episodeTagIds.value[episodeId] || []).includes(tagId) ||
    (episodeInheritedTagIds.value[episodeId] || []).includes(tagId)
  );
};

/**
 * Mirrors a star/unstar action into the LOCAL assignment map (the server does
 * the same in the star/unstar endpoints). Called by the star toggle sites
 * (context menu + swipe handlers) so anything reading the map — the combined
 * tag search, tag filters, Star's usage count in Settings — stays consistent
 * without a refetch.
 *
 * @param {number} episodeId - The episode's database id
 * @param {boolean} starred - The NEW starred state (true = just starred)
 */
const syncStarAssignment = (episodeId, starred) => {
  // Resolve the Star system tag; nothing to sync before tags are loaded
  const star = starTag.value;
  if (!star) return;

  const ids = episodeTagIds.value[episodeId] || [];
  if (starred && !ids.includes(star.id)) {
    // Starred: add the Star tag id to the episode's assignment list
    episodeTagIds.value[episodeId] = [...ids, star.id];
    star.episode_count = (star.episode_count || 0) + 1;
  } else if (!starred && ids.includes(star.id)) {
    // Unstarred: drop the Star tag id from the episode's assignment list
    episodeTagIds.value[episodeId] = ids.filter((id) => id !== star.id);
    star.episode_count = Math.max(0, (star.episode_count || 0) - 1);
  }
};

/**
 * Toggles a tag on an episode: attaches when absent, detaches when present.
 * Optimistically updates the local map (and the tag's episode_count) first,
 * then calls the API — reverting the local change if the request fails.
 *
 * @param {number} episodeId - The episode's database id
 * @param {Object} tag - The full tag object to toggle
 * @returns {Promise<boolean>} The new attached-state (true = now attached)
 */
const toggleEpisodeTag = async (episodeId, tag) => {
  const attached = episodeHasTag(episodeId, tag.id);

  // --- Optimistic local update (both directions) --------------------------
  if (attached) {
    // Detach: drop the tag id from the episode's list
    episodeTagIds.value[episodeId] = (episodeTagIds.value[episodeId] || []).filter(
      (id) => id !== tag.id,
    );
    // Keep the Settings usage count roughly in sync without a refetch
    tag.episode_count = Math.max(0, (tag.episode_count || 0) - 1);
  } else {
    // Attach: append the tag id (create the array on first tag)
    episodeTagIds.value[episodeId] = [...(episodeTagIds.value[episodeId] || []), tag.id];
    tag.episode_count = (tag.episode_count || 0) + 1;
  }

  // --- Server call, with rollback on failure ------------------------------
  try {
    if (attached) {
      await tagsApi.untagEpisode(tag.id, episodeId);
    } else {
      await tagsApi.tagEpisode(tag.id, episodeId);
    }
    return !attached;
  } catch (error) {
    console.error("Failed to toggle tag on episode:", error);
    // Revert the optimistic update: restore the previous attached-state
    if (attached) {
      episodeTagIds.value[episodeId] = [...(episodeTagIds.value[episodeId] || []), tag.id];
      tag.episode_count = (tag.episode_count || 0) + 1;
    } else {
      episodeTagIds.value[episodeId] = (episodeTagIds.value[episodeId] || []).filter(
        (id) => id !== tag.id,
      );
      tag.episode_count = Math.max(0, (tag.episode_count || 0) - 1);
    }
    notifyError(error.message);
    return attached;
  }
};

// =============================================================================
// Podcast tags (labels inherited by a podcast's episodes)
// =============================================================================

/**
 * Refetches the inherited episode→tags map from the server. Called after a
 * podcast's tags change, since the client can't cheaply recompute which
 * episodes belong to the podcast — the server does the join for us.
 */
const reloadInheritedAssignments = async () => {
  try {
    const pairs = await tagsApi.getInheritedTagAssignments();
    episodeInheritedTagIds.value = buildInheritedMap(pairs);
  } catch (error) {
    console.error("Failed to reload inherited tag assignments:", error);
  }
};

/**
 * Returns the user's tag objects labelling a podcast, alphabetically.
 *
 * @param {string} podcastId - The podcast id
 * @returns {Array} Array of tag objects (possibly empty)
 */
const podcastTagsFor = (podcastId) => {
  const ids = podcastTagIds.value[podcastId] || [];
  return userTags.value.filter((tag) => ids.includes(tag.id));
};

/**
 * Whether a specific tag currently labels a podcast.
 *
 * @param {string} podcastId - The podcast id
 * @param {number} tagId - The tag's database id
 * @returns {boolean} True when the tag labels the podcast
 */
const podcastHasTag = (podcastId, tagId) => {
  return (podcastTagIds.value[podcastId] || []).includes(tagId);
};

/**
 * Toggles a tag on a PODCAST: attaches when absent, detaches when present.
 * Optimistically updates the local podcast→tags map, then calls the API and
 * rolls back on failure. On success it refetches the inherited map so every
 * affected episode's chips update across all views.
 *
 * @param {string} podcastId - The podcast id
 * @param {Object} tag - The full tag object to toggle
 * @returns {Promise<boolean>} The new attached-state (true = now labels it)
 */
const togglePodcastTag = async (podcastId, tag) => {
  const attached = podcastHasTag(podcastId, tag.id);

  // --- Optimistic local update -------------------------------------------
  if (attached) {
    podcastTagIds.value[podcastId] = (podcastTagIds.value[podcastId] || []).filter(
      (id) => id !== tag.id,
    );
  } else {
    podcastTagIds.value[podcastId] = [...(podcastTagIds.value[podcastId] || []), tag.id];
  }

  // --- Server call, with rollback on failure -----------------------------
  try {
    if (attached) {
      await tagsApi.untagPodcast(podcastId, tag.id);
    } else {
      await tagsApi.tagPodcast(podcastId, tag.id);
    }
    // Success: refresh which episodes inherit which tags so chips update.
    await reloadInheritedAssignments();
    return !attached;
  } catch (error) {
    console.error("Failed to toggle tag on podcast:", error);
    // Revert the optimistic change
    if (attached) {
      podcastTagIds.value[podcastId] = [...(podcastTagIds.value[podcastId] || []), tag.id];
    } else {
      podcastTagIds.value[podcastId] = (podcastTagIds.value[podcastId] || []).filter(
        (id) => id !== tag.id,
      );
    }
    notifyError(error.message);
    return attached;
  }
};

// =============================================================================
// Tag CRUD (Settings management table + quick-create in the picker dialog)
// =============================================================================

/**
 * Creates a new user tag and inserts it into the local list (sorted).
 *
 * @param {string} name - The tag name
 * @returns {Promise<Object>} The created tag object
 * @throws {Error} On failure (e.g. 409 duplicate name) — caller shows the error
 */
const addTag = async (name) => {
  const tag = await tagsApi.createTag(name);
  // Append locally and restore the (system-first, then A→Z) ordering
  tags.value.push(tag);
  sortTags();
  return tag;
};

/**
 * Renames one of the user's tags and updates the local list (sorted).
 *
 * @param {Object} tag - The tag object to rename
 * @param {string} name - The new name
 * @throws {Error} On failure (409 duplicate, 403 system tag) — caller shows it
 */
const renameTag = async (tag, name) => {
  const updated = await tagsApi.updateTag(tag.id, name);
  // Apply the server-confirmed name locally and re-sort the list
  const local = tags.value.find((t) => t.id === tag.id);
  if (local) local.name = updated.name;
  sortTags();
};

/**
 * Deletes one of the user's tags: removes it from the local list AND strips
 * its id from every episode's assignment list so chips disappear immediately.
 *
 * @param {Object} tag - The tag object to delete
 * @throws {Error} On failure (403 system tag) — caller shows the error
 */
const removeTag = async (tag) => {
  await tagsApi.deleteTag(tag.id);
  // Drop the tag from the local tag list
  tags.value = tags.value.filter((t) => t.id !== tag.id);
  // Strip the deleted tag id from every episode's assignment list
  // (server-side this happened via ON DELETE CASCADE)
  for (const episodeId of Object.keys(episodeTagIds.value)) {
    const ids = episodeTagIds.value[episodeId];
    if (ids.includes(tag.id)) {
      episodeTagIds.value[episodeId] = ids.filter((id) => id !== tag.id);
    }
  }
  // Also strip it from the podcast-tag and inherited maps (CASCADE removed the
  // podcast_tags rows server-side too), so podcast chips and inherited episode
  // chips disappear immediately without a refetch.
  for (const podcastId of Object.keys(podcastTagIds.value)) {
    const ids = podcastTagIds.value[podcastId];
    if (ids.includes(tag.id)) {
      podcastTagIds.value[podcastId] = ids.filter((id) => id !== tag.id);
    }
  }
  for (const episodeId of Object.keys(episodeInheritedTagIds.value)) {
    const ids = episodeInheritedTagIds.value[episodeId];
    if (ids.includes(tag.id)) {
      episodeInheritedTagIds.value[episodeId] = ids.filter((id) => id !== tag.id);
    }
  }
};

// =============================================================================
// Composable export
// =============================================================================

/**
 * Composable function returning all tag state and actions.
 *
 * @returns {Object} Tag state refs, computed properties, and action functions
 */
export function useTags() {
  return {
    // state
    tags,
    userTags,
    userTagsByUsage,
    userTagsByPodcastUsage,
    tagsByUsage,
    starTag,
    episodeTagIds,
    podcastTagIds,
    episodeInheritedTagIds,
    loadingTags,
    tagsLoaded,
    tagsSnackbar,
    // loading
    loadTags,
    reloadInheritedAssignments,
    // per-episode helpers
    tagsForEpisode,
    episodeHasTag,
    episodeHasTagEffective,
    toggleEpisodeTag,
    syncStarAssignment,
    // per-podcast helpers
    podcastTagsFor,
    podcastHasTag,
    togglePodcastTag,
    // CRUD
    addTag,
    renameTag,
    removeTag,
  };
}
